import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowRight, TrendingUp, Sparkles } from 'lucide-react';
import BrandLogoBar from '@/components/BrandLogoBar';

const HOME_BG = "https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/iq74uoo5_ChatGPT%20Image%2022%20juil.%202026%2C%2008_24_31.png";

const ChallengesIntroPage = () => {
  const navigate = useNavigate();

  const trainingAreas = [
    "Gestion du débit inspiratoire et expiratoire",
    "Entrainement à la tolérance au CO₂",
    "Gestion émotionnelle de la sensation de soif d'air",
    "Capacité à expirer complètement",
    "Capacité à inspirer complètement",
    "Diminuer la Fréquence Respiratoire à son minimum",
    "Apprendre à activer son diaphragme mais surtout à le relâcher complètement",
  ];

  const glassCard = {
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(18px) saturate(1.2)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.2)',
    border: '1px solid rgba(255,255,255,0.18)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 20px 40px -18px rgba(0,0,0,0.4)',
  };

  return (
    <div
      className="min-h-screen pt-10 pb-32 sm:pb-40 px-4 sm:px-6 relative"
      style={{
        backgroundImage: `url('${HOME_BG}')`,
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        fontFamily: 'Figtree, sans-serif',
      }}
      data-testid="challenges-intro-page"
    >
      <div className="max-w-3xl mx-auto relative z-10">
        <BrandLogoBar className="mb-4" />
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full shadow-xl mb-5"
            style={{
              background: 'linear-gradient(135deg, #FBBF24 0%, #F97316 100%)',
              boxShadow: '0 0 40px rgba(251, 146, 60, 0.5), 0 0 80px rgba(251, 191, 36, 0.25)',
            }}
          >
            <Trophy className="w-10 h-10 text-white" strokeWidth={1.6} />
          </div>
          <h1
            className="text-4xl sm:text-5xl mb-2 text-white"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 300,
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
            }}
            data-testid="challenges-intro-title"
          >
            Défis <span style={{ color: '#FBBF24', fontWeight: 400 }}>Respiratoires</span>
          </h1>
        </div>

        {/* Explanation */}
        <div className="rounded-2xl p-6 sm:p-8 mb-6" style={glassCard}>
          <p className="text-white/90 leading-relaxed mb-5">
            Les défis respiratoires sont des outils d&apos;entraînement et de testing/suivi de vos progrès dans les différents aspects de la maîtrise de la respiration&nbsp;:
          </p>
          <ol className="space-y-2.5 text-white/85" data-testid="training-areas-list">
            {trainingAreas.map((area, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #FBBF24 0%, #F97316 100%)' }}
                >
                  {idx + 1}
                </span>
                <span className="pt-0.5">{area}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Progress tracking */}
        <div className="rounded-2xl p-6 sm:p-8 mb-6" style={glassCard}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5" style={{ color: '#FBBF24' }} strokeWidth={1.8} />
            <h2 className="text-lg font-semibold text-white">Suivi de vos progrès</h2>
          </div>
          <p className="text-white/85 leading-relaxed">
            Chaque défi réalisé est enregistré dans votre historique, sous forme de graphique qui vous permet de suivre l&apos;évolution de vos progrès au fil du temps. Vous y verrez vos records personnels, le temps passé à pratiquer, et monitorer votre progression.
          </p>
        </div>

        {/* Philosophy */}
        <div className="rounded-2xl p-6 sm:p-8 mb-6" style={glassCard}>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5" style={{ color: '#FBBF24' }} strokeWidth={1.8} />
            <h2 className="text-lg font-semibold text-white">Une pratique transformative</h2>
          </div>
          <div className="space-y-4 text-white/85 leading-relaxed">
            <p>
              Chaque entraînement est une reprogrammation profonde de votre physiologie, de votre mental, et crée, au fil du temps, une rééquilibration parfaite de votre système nerveux, une transformation progressive de votre mental et de la gestion de vos émotions, une amélioration constante de votre confiance dans les capacités du corps, et une diminution exponentielle de votre essoufflement…
            </p>
            <p>
              Pratiquez ces exercices comme un jeu, avec une attitude curieuse dans l&apos;exploration sensorielle des sensations corporelles à chaque instant, sans chercher à atteindre quoi que ce soit, sans intention.
            </p>
            <p
              className="text-center italic font-semibold"
              style={{ color: '#FBBF24', textShadow: '0 0 20px rgba(251,191,36,0.4)' }}
              data-testid="intention-quote"
            >
              N&apos;oubliez pas&nbsp;: Intention = Tension !
            </p>
            <p className="text-center font-medium text-white">Bonne pratique&nbsp;!</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <button
            onClick={() => navigate('/breathing/practice?mode=challenge')}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-lg font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, #FBBF24 0%, #F97316 100%)',
              boxShadow: '0 0 30px rgba(251,146,60,0.55), 0 10px 24px rgba(0,0,0,0.3)',
            }}
            data-testid="go-to-challenges-btn"
          >
            Vers les défis
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChallengesIntroPage;
